/**
 * @fileoverview Sistema de logging inteligente para GraphicForce
 * Permite diferentes niveles de log y configuración por ambiente
 */

// Niveles de logging
const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3
};

// Configuración por ambiente
const getLogLevel = () => {
  // En desarrollo: mostrar todos los logs
  if (process.env.NODE_ENV === 'development') {
    return LOG_LEVELS.DEBUG;
  }
  
  // En producción: solo errores y warnings críticos
  if (process.env.NODE_ENV === 'production') {
    return LOG_LEVELS.WARN;
  }
  
  // Fallback
  return LOG_LEVELS.INFO;
};

// Colores para diferentes tipos de log (solo en desarrollo)
const LOG_COLORS = {
  ERROR: '#ff4757', // Rojo
  WARN: '#ffa502',  // Naranja
  INFO: '#3742fa',  // Azul
  DEBUG: '#2ed573'  // Verde
};

// Formatter para logs más legibles
const formatMessage = (level, module, message, data = null) => {
  const timestamp = new Date().toLocaleTimeString();
  const prefix = `[${timestamp}] [${level}] [${module}]`;
  
  if (process.env.NODE_ENV === 'development') {
    return {
      prefix,
      message,
      data,
      color: LOG_COLORS[level]
    };
  }
  
  return `${prefix} ${message}`;
};

// Logger principal
class Logger {
  constructor() {
    this.logLevel = getLogLevel();
  }
  
  // Log de error (siempre se muestra)
  error(module, message, data = null) {
    if (this.logLevel >= LOG_LEVELS.ERROR) {
      const formatted = formatMessage('ERROR', module, message, data);
      
      if (process.env.NODE_ENV === 'development') {
        console.group(`%c${formatted.prefix}`, `color: ${formatted.color}; font-weight: bold;`);
        console.error(formatted.message);
        if (formatted.data) console.error('Datos:', formatted.data);
        console.groupEnd();
      } else {
        console.error(formatted);
        if (data) console.error(data);
      }
    }
  }
  
  // Log de warning
  warn(module, message, data = null) {
    if (this.logLevel >= LOG_LEVELS.WARN) {
      const formatted = formatMessage('WARN', module, message, data);
      
      if (process.env.NODE_ENV === 'development') {
        console.group(`%c${formatted.prefix}`, `color: ${formatted.color}; font-weight: bold;`);
        console.warn(formatted.message);
        if (formatted.data) console.warn('Datos:', formatted.data);
        console.groupEnd();
      } else {
        console.warn(formatted);
        if (data) console.warn(data);
      }
    }
  }
  
  // Log informativo
  info(module, message, data = null) {
    if (this.logLevel >= LOG_LEVELS.INFO) {
      const formatted = formatMessage('INFO', module, message, data);
      
      if (process.env.NODE_ENV === 'development') {
        console.group(`%c${formatted.prefix}`, `color: ${formatted.color}; font-weight: bold;`);
        console.info(formatted.message);
        if (formatted.data) console.info('Datos:', formatted.data);
        console.groupEnd();
      } else {
        console.info(formatted);
      }
    }
  }
  
  // Log de debugging (solo en desarrollo)
  debug(module, message, data = null) {
    if (this.logLevel >= LOG_LEVELS.DEBUG) {
      const formatted = formatMessage('DEBUG', module, message, data);
      
      if (process.env.NODE_ENV === 'development') {
        console.group(`%c${formatted.prefix}`, `color: ${formatted.color}; font-weight: bold;`);
        console.log(formatted.message);
        if (formatted.data) console.log('Datos:', formatted.data);
        console.groupEnd();
      }
      // En producción no se muestran logs de debug
    }
  }
  
  // Métodos de conveniencia para operaciones específicas
  operation(module, operation, data = null) {
    this.info(module, `Operación: ${operation}`, data);
  }
  
  success(module, message, data = null) {
    if (process.env.NODE_ENV === 'development') {
      const timestamp = new Date().toLocaleTimeString();
      console.log(`%c[${timestamp}] [✓] [${module}] ${message}`, 
                  'color: #2ed573; font-weight: bold;');
      if (data) console.log('Datos:', data);
    }
  }
  
  // Helper para medición de performance
  time(label) {
    if (this.logLevel >= LOG_LEVELS.DEBUG) {
      console.time(label);
    }
  }
  
  timeEnd(label) {
    if (this.logLevel >= LOG_LEVELS.DEBUG) {
      console.timeEnd(label);
    }
  }
}

// Singleton del logger
const logger = new Logger();

// Factory para crear loggers genéricos
export const createModuleLogger = (moduleName) => ({
  error: (message, data) => logger.error(moduleName, message, data),
  warn: (message, data) => logger.warn(moduleName, message, data),
  info: (message, data) => logger.info(moduleName, message, data),
  debug: (message, data) => logger.debug(moduleName, message, data),
  operation: (operation, data) => logger.operation(moduleName, operation, data),
  success: (message, data) => logger.success(moduleName, message, data)
});

// Logger por defecto
export default logger;
